import React, { JSX } from 'react';
import {
  Column,
  useFilters,
  usePagination,
  useSortBy,
  useTable
} from 'react-table';

import { useSticky } from 'react-table-sticky';
import Pagination from './Pagination';
import Icon from '@components/atoms/icon';
import { CURRENT_YEAR } from '@lib/constants';

type TableType = {
  data: Array<any>;
  columns: Array<Column>;
  initialPageSize?: number;
  initialPageIndex?: number;
  hiddenColumns: string[];
  resendId?: string | null;
  includePagination?: boolean;
  showFilters: boolean;
  sortBy?: any;
  isSearchActive?: boolean;
};

const Table = ({
  columns,
  data,
  initialPageSize,
  initialPageIndex,
  hiddenColumns,
  includePagination,
  showFilters,
  sortBy
}: TableType) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      disableSortRemove: true,
      initialState: {
        pageIndex: initialPageIndex,
        pageSize: initialPageSize,
        hiddenColumns,
        ...(sortBy && { sortBy })
      }
    },
    useFilters,
    useSortBy,
    usePagination,
    useSticky
  );

  return (
    <div className="scrollbar-hide overflow-y-scroll w-full rounded-lg">
      <table className="bg-white w-full shadow-100" {...getTableProps()}>
        <thead className="sticky -top-1 left-0 bg-white">
          {headerGroups.map(
            (
              headerGroup: {
                getHeaderGroupProps: () => JSX.IntrinsicAttributes &
                  React.ClassAttributes<HTMLTableRowElement> &
                  React.HTMLAttributes<HTMLTableRowElement>;
                headers: any[];
              },
              index
            ) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                key={`table-main-row-${index}`}
              >
                {headerGroup.headers.map((column, i) => {
                  return (
                    <th
                      key={`${column.Header}-${i}`}
                      className={`pt-4 pb-2 px-4 text-sm lg:min-w-[100px] bg-white ${
                        column.sticky === 'left' ? 'sticky z-10 left-0' : ''
                      }`}
                    >
                      <div className="flex flex-row items-center justify-start w-full">
                        <div
                          className={`flex items-center ${
                            column.isSorted ? 'cursor-pointer' : ''
                          } font-bold font-open-sans text-neutral-600 text-xs`}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          {column.render('Header')}
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <div
                                className="w-5 h-5"
                                data-testid="table-sort-chevron-up"
                              >
                                <Icon name="ChevronUp" />
                              </div>
                            ) : (
                              <div
                                className="w-5 h-5"
                                data-testid="table-sort-chevron-down"
                              >
                                <Icon name="ChevronDown" />
                              </div>
                            )
                          ) : (
                            ''
                          )}
                        </div>
                        {showFilters && (
                          <div className="max-w-[50%] ml-4">
                            {column.canFilter ? column.render('Filter') : null}
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            )
          )}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(
            (
              row: {
                getRowProps: () => JSX.IntrinsicAttributes &
                  React.ClassAttributes<HTMLTableRowElement> &
                  React.HTMLAttributes<HTMLTableRowElement>;
                cells: any[];
                values: { [key: string]: any };
              },
              index: number
            ) => {
              prepareRow(row);

              if (page.length === index + 1) {
                return (
                  <React.Fragment key={`table-last-row-${index}`}>
                    <tr
                      className="border-b border-neutral-100 bg-white"
                      {...row.getRowProps()}
                    >
                      {row.cells.map((cell, index) => {
                        return (
                          <td
                            key={`table-index-one-${index}`}
                            className={`truncate px-4 py-4 font-normal bg-white ${
                              cell.column.sticky === 'left'
                                ? 'sticky z-10 left-0'
                                : ''
                            }`}
                            {...cell.getCellProps()}
                          >
                            <div>{cell.render('Cell')}</div>
                          </td>
                        );
                      })}
                    </tr>

                    <tr className="bg-neutral-800 text-white text-center">
                      <td colSpan={row.cells.length}>
                        <span className="text-xs">
                          &copy;
                          {` Fantasy Football Hub ${CURRENT_YEAR}`}
                        </span>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              } else {
                return (
                  <tr
                    key={`table-row-one-${index}`}
                    className="border-b border-neutral-100 bg-white"
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td
                          key={`table-index-two-${index}`}
                          className={`truncate px-4 py-4 text-neutral-800 font-normal bg-white ${
                            cell.column.sticky === 'left'
                              ? 'sticky z-10 left-0'
                              : ''
                          }`}
                          {...cell.getCellProps()}
                        >
                          <div>{cell.render('Cell')}</div>
                        </td>
                      );
                    })}
                  </tr>
                );
              }
            }
          )}
        </tbody>
      </table>
      {includePagination && (
        <Pagination
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
          pageCount={pageCount}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default Table;
